import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Button (shadcn/ui new-york style) via CVA + Radix Slot.
 * Usa CSS custom properties do design system Órbita para todas as cores,
 * garantindo que o mecanismo [data-theme] do white-label funcione sem
 * modificar os componentes (ADR-008).
 */
const buttonVariants = cva(
  // Base
  [
    "inline-flex items-center justify-center gap-2",
    "whitespace-nowrap rounded-sm text-sm font-medium",
    "transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        // Primario: acento do tenant (gold-deep em light, nectar em dark)
        default: [
          "bg-[var(--color-accent)] text-[var(--color-bg-surface)]",
          "hover:bg-[var(--color-accent-hover)]",
          "focus-visible:ring-[var(--color-accent)]",
        ].join(" "),
        // Destructive
        destructive: [
          "bg-[var(--color-destructive)] text-[var(--bone)]",
          "hover:opacity-90",
          "focus-visible:ring-[var(--color-destructive)]",
        ].join(" "),
        // Contorno
        outline: [
          "border border-[var(--color-bg-border)]",
          "bg-transparent text-[var(--color-text-primary)]",
          "hover:bg-[var(--color-bg-raised)]",
          "focus-visible:ring-[var(--color-accent)]",
        ].join(" "),
        // Secundário
        secondary: [
          "bg-[var(--color-bg-raised)] text-[var(--color-text-primary)]",
          "hover:bg-[var(--color-bg-border)]",
          "focus-visible:ring-[var(--color-accent)]",
        ].join(" "),
        // Ghost: sem fundo
        ghost: [
          "bg-transparent text-[var(--color-text-primary)]",
          "hover:bg-[var(--color-bg-raised)]",
          "focus-visible:ring-[var(--color-accent)]",
        ].join(" "),
        // Link
        link: [
          "bg-transparent text-[var(--color-accent)] underline-offset-4",
          "hover:underline hover:text-[var(--color-accent-hover)]",
          "focus-visible:ring-[var(--color-accent)]",
        ].join(" "),
      },
      size: {
        default: "h-9 px-4 py-2",
        sm:      "h-8 px-3 text-xs",
        lg:      "h-10 px-6",
        icon:    "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonVariants {
  /** Renderiza o filho como elemento raiz via Radix Slot (composição). */
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components -- padrão shadcn: CVA variant helper exportado junto com o componente
export { Button, buttonVariants };
